import Typography from "antd/lib/typography";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import { Inbox01 } from "@sol/uikit/core/icons/Communication/Inbox01";
import { Upload, type UploadProps } from "@sol/uikit/data-entry/Upload/Upload";
import { message } from "@sol/uikit/feedback/Message/Message";
import { Button } from "@sol/uikit/general/Button/Button";

import { ClassroomLearnersFormValues } from "./ClassroomLearners";

// React dropzone files mimetypes might not be reconized on certain os (windows and osx)
// We add application/vnd.ms-excel and .csv to be sure csv formats are accepted
export const SUPPORTED_FORMATS = ["text/csv", "application/vnd.ms-excel", ".csv"];

type Props = {
    onCancel: () => void;
    onSubmit: () => void;
    className?: string;
};

export const ClassroomLearnersFileImport = ({ onSubmit, className, onCancel }: Props) => {
    const [t] = useTranslation();
    const [messageApi, contextHolder] = message.useMessage();

    const {
        watch,
        setValue,
        setError,
        clearErrors,
        formState: { errors },
    } = useFormContext<ClassroomLearnersFormValues>();

    const file = watch("file");

    const handleFileChange = (info: any) => {
        const { file, fileList } = info;

        if (fileList.length === 0) {
            setValue("file", undefined);
            clearErrors("file");
            return;
        }

        if (file && file.status !== "removed") {
            const isCSV = SUPPORTED_FORMATS.some(
                format => file.type === format || file.name.toLowerCase().endsWith(".csv"),
            );

            if (isCSV) {
                setValue("file", file);
                clearErrors("file");
                messageApi.success(t("component.ClassroomLearnersFileImport.file.success"));
            } else {
                setError("file", { type: "fileFormat" });
                messageApi.error(t("component.ClassroomLearnersFileImport.file.errors.fileFormat"), 6);
            }
        }
    };

    const handleRemove = () => {
        setValue("file", undefined);
        clearErrors("file");
    };

    const uploadProps: UploadProps = {
        name: "file",
        multiple: false,
        accept: SUPPORTED_FORMATS.join(","),
        showUploadList: true,
        maxCount: 1, // Only allow one file at a time
        fileList: file
            ? [
                  {
                      uid: uuidv4(),
                      name: file.name,
                      status: "done" as const,
                  },
              ]
            : [],
        beforeUpload: () => false, // Prevent auto upload
        onChange: handleFileChange,
        onRemove: handleRemove,
    };

    return (
        <form onSubmit={onSubmit} className={className}>
            <div className="flex flex-col gap-6">
                <div className="flex flex-col gap-3">
                    <Typography.Title level={2} className="text-ant-h2">
                        {t("component.ClassroomLearnersFileImport.title")}
                    </Typography.Title>
                    <Typography.Text className="text-ant-lg">
                        {t("component.ClassroomLearnersFileImport.subtitle.label")}
                        <br />
                        <Typography.Link href="/static/csv/classroom-learners-template.csv" download>
                            {t("component.ClassroomLearnersFileImport.subtitle.template")}
                        </Typography.Link>
                    </Typography.Text>
                </div>

                <>
                    <Upload.Dragger {...uploadProps}>
                        <p className="ant-upload-drag-icon inline-flex items-center text-color-ant-primary">
                            <Inbox01 size={32} />
                        </p>
                        <p className="ant-upload-text">
                            {t("component.ClassroomLearnersFileImport.file.import.label")}
                        </p>
                        <p className="ant-upload-hint cursor-pointer underline">
                            {t("component.ClassroomLearnersFileImport.file.import.link")}
                        </p>
                    </Upload.Dragger>

                    {errors.file && (
                        <Typography.Text type="danger">
                            {t(`component.ClassroomLearnersFileImport.file.errors.${errors.file.type}`)}
                        </Typography.Text>
                    )}
                </>

                <div className="flex flex-row justify-between">
                    <Button htmlType="button" data-cy="classroom-learner-imports-cancel" onClick={onCancel}>
                        {t("component.ClassroomLearnersFileImport.cancel")}
                    </Button>
                    <Button
                        disabled={!file}
                        htmlType="submit"
                        type="primary"
                        data-cy="classroom-learners-submit-button"
                    >
                        {t("form.addLearners.submit")}
                    </Button>
                </div>
            </div>
            {contextHolder}
        </form>
    );
};
